<template>
  <div class="container">
    <div v-if="!isLoading && data !== null">
      <bars
        :chartData="data.consumption"
        :title="$t('statistics.consumption')"
        :options="consumptionOptions"
        :sum="getSum(data.consumption).toFixed(0) + ' kWh'"
      ></bars>
      <br /><br />
      <lines
        :chartData="data.cost"
        :title="$t('statistics.cost')"
        :options="costOptions"
        :sum="decimal(getSum(data.cost)) + ' ' + currencyLabel"
      ></lines>
      <br /><br />
      <lines
        :chartData="data.saving"
        :title="$t('statistics.saving')"
        :options="savingOptions"
        :sum="decimal(getSum(data.saving)) + ' ' + currencyLabel"
      ></lines>
      <br /><br />

      <div v-if="mode === 0">
        <lines
          :chartData="data.temperature"
          :title="$t('statistics.temperature')"
          :options="temperatureOptions"
        ></lines>
      </div>
      <div v-else>
        <lines
          :chartData="data.temperature_max"
          :title="$t('statistics.temperature_max')"
          :options="temperatureOptions"
        ></lines>
        <br /><br />
        <lines
          :chartData="data.temperature_min"
          :title="$t('statistics.temperature_min')"
          :options="temperatureOptions"
        ></lines>
      </div>
      <br /><br /><br /><br /><br /><br /><br /><br />
    </div>

    <div v-if="noDevices">
      {{ $t("statistics.noDevices") }}
    </div>

    <div class="modal" v-bind:class="{ 'is-active': showDayDatepicker }">
      <div class="modal-background" @click="showDayDatepicker = false"></div>
      <div class="modal-content">
        <b-datepicker
          :min-date="new Date(2010, 0, 1)"
          :max-date="currentDate"
          inline
          :value="chosenDate"
          @input="setDate"
        >
        </b-datepicker>
      </div>
      <button
        class="modal-close is-large"
        aria-label="close"
        @click="showMonthDatepicker = false"
      ></button>
    </div>
    <div class="modal" v-bind:class="{ 'is-active': showMonthDatepicker }">
      <div class="modal-background" @click="showMonthDatepicker = false"></div>
      <div class="modal-content">
        <month-picker
          v-if="monthPickerShow"
          ref="monthPicker"
          :default-month="chosenDate.getMonth() + 1"
          :default-year="chosenDate.getFullYear()"
          :months="months"
          @input="setDateMonth"
        ></month-picker>
      </div>
      <button
        class="modal-close is-large"
        aria-label="close"
        @click="showMonthDatepicker = false"
      ></button>
    </div>

    <div id="datePickerContent">
      <section id="datePicker" v-if="!noDevices">
        <b-tabs position="is-centered" v-model="mode">
          <b-tab-item :label="$t('statistics.mode.daily')">
            <div class="date-label" @click="showDayDatepicker = true">
              {{ dayDateLabel }}
            </div>
            <br />
            <ul>
              <li
                v-bind:class="{
                  dayActive: d === day,
                  dayInactive: isDayInactive(d),
                }"
                v-on="!isDayInactive(d) ? { click: () => setDay(d) } : {}"
                class="daySelector"
                v-for="d in days"
              >
                {{ d }}
              </li>
            </ul>
            <br />
          </b-tab-item>
          <b-tab-item :label="$t('statistics.mode.monthly')">
            <div
              class="date-label"
              @click="
                showMonthDatepicker = true;
                monthPickerShow = true;
              "
            >
              {{ monthDateLabel }}
            </div>
            <br />
            <ul>
              <li
                v-bind:class="{
                  monthActive: index + 1 === month,
                  monthInactive: isMonthInactive(index),
                }"
                v-on="
                  !isMonthInactive(index)
                    ? { click: () => setMonth(index + 1) }
                    : {}
                "
                class="monthSelector"
                v-for="(m, index) in months"
              >
                {{ m.slice(0, 3) }}
              </li>
            </ul>
            <br />
          </b-tab-item>
          <b-tab-item :label="$t('statistics.mode.annual')">
            <div class="date-label" style="cursor: default">
              {{ year }}
            </div>
            <br />
            <ul>
              <li
                v-bind:class="{ yearActive: y === year }"
                @click="setYear(y)"
                class="yearSelector"
                v-for="y in years"
              >
                {{ y }}
              </li>
            </ul>
            <br />
          </b-tab-item>
        </b-tabs>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import constants from "../../config/constants";
import zapp from "../../util/zapp";

import Bars from "../charts/bars";
import Lines from "../charts/lines";

export default {
  components: {
    Bars,
    Lines,
  },
  name: "statistics",
  props: ["currency"],

  data() {
    let now = new Date();
    let days = this.getDays(now.getFullYear(), now.getMonth() + 1);
    let months = constants.months;
    let years = zapp.range(2010, now.getFullYear());

    let currency = constants.currency[this.currency];

    if (!currency) currency = "€";

    return {
      currencyLabel: currency,
      consumptionOptions: {
        legend: {
          labels: {
            fontColor: "white",
            fontSize: 18,
          },
        },

        chartArea: {
          backgroundColor: "#2daa66",
        },

        tooltips: {
          callbacks: {
            label: function (tooltipItems, data) {
              return (
                data.datasets[0].data[tooltipItems.index].toLocaleString(
                  navigator.language,
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 5,
                  }
                ) + " kwh "
              );
            },
          },
        },

        scales: {
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return (
                    value.toLocaleString(navigator.language, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 5,
                    }) + " kwh "
                  );
                },
              },
              gridLines: {
                display: true,
              },
            },
          ],
        },
      },
      costOptions: {
        legend: {
          labels: {
            fontColor: "white",
            fontSize: 18,
          },
        },

        chartArea: {
          backgroundColor: "#be7fb3",
        },

        tooltips: {
          callbacks: {
            label: function (tooltipItems, data) {
              return (
                zapp.getFormattedDecimal(
                  data.datasets[0].data[tooltipItems.index]
                ) +
                " " +
                currency +
                " "
              );
            },
          },
        },

        scales: {
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return zapp.getFormattedDecimal(value) + " " + currency + " ";
                },
              },
              gridLines: {
                display: true,
              },
            },
          ],
        },
      },
      savingOptions: {
        legend: {
          labels: {
            fontColor: "white",
            fontSize: 18,
          },
        },

        chartArea: {
          backgroundColor: "#7ab5df",
        },

        tooltips: {
          callbacks: {
            label: function (tooltipItems, data) {
              return (
                zapp.getFormattedDecimal(
                  data.datasets[0].data[tooltipItems.index]
                ) +
                " " +
                currency +
                " "
              );
            },
          },
        },

        scales: {
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return zapp.getFormattedDecimal(value) + " " + currency + " ";
                },
              },
              gridLines: {
                display: true,
              },
            },
          ],
        },
      },
      temperatureOptions: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            fontColor: "white",
            fontSize: 18,
            usePointStyle: true,
            padding: 30,
          },
        },

        tooltips: {
          callbacks: {
            label: function (tooltipItems, data) {
              return (
                zapp.getFormattedDecimal(
                  data.datasets[0].data[tooltipItems.index]
                ) + "º "
              );
            },
          },
        },

        chartArea: {
          backgroundColor: "#eeb62b",
        },

        scales: {
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return value + "º ";
                },
              },
              gridLines: {
                display: true,
              },
            },
          ],
        },
      },
      isLoading: true,
      noDevices: false,
      monthPickerShow: false,
      showDayDatepicker: false,
      showMonthDatepicker: false,
      mode: constants.statisticsModes.daily,
      days: days,
      months: months,
      years: years,
      currentDate: now,
      day: now.getDate(),
      month: now.getMonth() + 1,
      year: now.getFullYear(),
    };
  },
  computed: {
    dayDateLabel: function () {
      return (
        this.day + " " + constants.months[this.month - 1] + " " + this.year
      );
    },

    monthDateLabel: function () {
      return constants.months[this.month - 1] + " " + this.year;
    },

    chosenDate: function () {
      return new Date(this.year, this.month - 1, this.day);
    },

    ...mapState("statistics", ["loading", "data"]),

    ...mapState("installations", {
      installation: "current",
    }),
  },
  methods: {
    decimal: (number) => zapp.getFormattedDecimal(number),

    getSum: function (data) {
      return data.datasets[0].data.reduce((a, b) => a + b, 0);
    },

    getData: function () {
      this.isLoading = true;
      this.noDevices = false;

      this.$parent
        .setStatistics({
          mode: this.mode,
          day: (this.day < 10 ? "0" : "") + this.day,
          month: (this.month < 10 ? "0" : "") + this.month,
          year: this.year,
        })
        .then(() => {
          this.isLoading = false;
          this.monthPickerShow = false; //RELOAD MONTHPICKER COMPONENT
        })
        .catch(() => {
          this.noDevices = true;
        });
    },

    //DAYS
    getDays: function (year, month) {
      let d = new Date(year, month, 0);
      return zapp.range(1, d.getDate());
    },

    setDay: function (day) {
      this.day = day;
      this.getData();
    },

    isDayInactive: function (d) {
      let day = new Date(this.year, this.month - 1, d);
      return day > this.currentDate;
    },

    setDate: function (date) {
      this.day = date.getDate();
      this.month = date.getMonth() + 1;
      this.year = date.getFullYear();
      if (this.showDayDatepicker) this.getData();
      this.showDayDatepicker = false;
    },

    //MONTHS
    setMonth: function (month) {
      this.month = month;
      this.getData();
    },

    isMonthInactive: function (m) {
      let month = new Date(this.year, m, 1);
      return month > this.currentDate;
    },

    setDateMonth: function (date) {
      this.month = date.monthIndex;
      this.year = date.year;
      this.showMonthDatepicker = false;
      this.getData();
    },

    //YEAR

    setYear: function (year) {
      this.year = year;
      this.getData();
    },
  },

  watch: {
    mode: function () {
      this.days = this.getDays(this.year, this.month);
      this.getData();
    },

    month: function () {
      if (this.isDayInactive(this.day)) this.day = this.currentDate.getDate();
    },
  },

  mounted() {
    this.getData();
  },
};
</script>

<style lang="scss">
#datePicker {
  .b-tabs .tab-content {
    padding: 0 !important;
  }

  .tabs {
    ul {
      border-bottom-color: transparent;
    }

    li a {
      border-bottom-color: transparent;
    }

    .is-active a {
      color: black !important;
      font-weight: bold;
      border-bottom: 2px solid black;
    }
  }
}

.dropdown.is-inline .dropdown-menu {
  display: block !important;
}

.month-picker-container {
  width: 100% !important;
  padding: 5%;

  .month-picker-year {
    height: 60px;
  }

  .month-picker-month {
    flex-basis: calc(33.333%) !important;
  }
}
</style>

<style lang="scss" scoped>
.container {
  max-width: 100%;
}

#datePickerContent {
  position: fixed;
  left: 0;
}

#datePicker {
  position: fixed;
  padding-left: 0 !important;
  width: 83.5%;
  bottom: 0;
  margin-left: 16.6667%;
  background-color: rgba(248, 248, 248, 0.52);
}

.daySelector,
.monthSelector,
.yearSelector {
  display: inline;
  margin: 0.5em;
  cursor: pointer;
}

.daySelector:hover,
.monthSelector:hover,
.yearSelector:hover {
  font-weight: bold;
}

.dayActive,
.monthActive,
.yearActive {
  background-color: #67a969;
  padding: 0.5em;
  border-radius: 50%;
  color: white;
  font-weight: bold;
  cursor: default;
}

.dayInactive,
.monthInactive {
  color: grey;
  cursor: default;
}

.date-label {
  background-color: #d8d8d8;
  font-weight: bold;
  text-align: left;
  padding-left: 1em;
  margin-top: 1em;
  border-left: 3px solid black;
  cursor: pointer;
}

.modal-content {
  background-color: white;
}

@media screen and (max-width: 1087px) {
  #datePicker {
    width: 100%;
    margin-left: 0;
  }
}
</style>
